import { useMutation, useQuery } from "@apollo/client/react";
import { CloseOutlined, DeleteOutlined, ExperimentOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { App, Button, Flex, Input, Space, Table, Tooltip, Typography } from "antd";
import { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { TableEmptyState } from "../../components/TableEmptyState.tsx";
import { tableScroll, tableSortDirections, tableSticky } from "../../layout/constants.ts";
import {
  DeleteIngredientDocument,
  IngredientSortField,
  ListIngredientsDocument,
} from "../../gql/generated/graphqlSchema.tsx";
import { useDebouncedSearch } from "../../hooks/useDebouncedSearch.ts";
import { useSafeSearchParams } from "../../hooks/useSafeSearchParams.ts";
import {
  buildSearchUrl,
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  tableSearchParams,
  useTableParams,
} from "../../hooks/useTableParams.tsx";
import { routes } from "../../routes.ts";
import { formatDateTime } from "../../utils/datetime.ts";
import { CreateIngredientModal } from "./CreateIngredientModal.tsx";
import { useTrailRoot } from "../../hooks/useTrailRoot.ts";

const columnToSortField = {
  name: IngredientSortField.Name,
  createdAt: IngredientSortField.CreatedAt,
  updatedAt: IngredientSortField.UpdatedAt,
};

const searchParamsSchema = tableSearchParams(columnToSortField).extend({
  q: z.string().optional(),
});

export const IngredientsPage = () => {
  const { t, i18n } = useTranslation();
  useTrailRoot(t("ingredients.title"), ExperimentOutlined);
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const { q: search, page, pageSize, sortField, sortDirection } = useSafeSearchParams(searchParamsSchema);

  const table = useTableParams({
    pageSize,
    page,
    sortField,
    sortDirection,
    columnToSortField,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  const { data, loading, refetch } = useQuery(ListIngredientsDocument, {
    variables: {
      search,
      pagination: {
        offset: table.offset,
        limit: table.limit,
        sortField,
        sortDirection,
      },
    },
    fetchPolicy: "network-only",
  });

  const [deleteIngredient] = useMutation(DeleteIngredientDocument, {
    onCompleted: () => {
      void message.success(t("ingredients.deletedDone"));
      void refetch();
    },
  });

  const handleSearch = useCallback(
    (value: string) => {
      void navigate(
        buildSearchUrl({
          q: value || undefined,
          pageSize: pageSize === DEFAULT_PAGE_SIZE ? undefined : String(pageSize),
          sortField,
          sortDirection,
        }),
      );
    },
    [navigate, pageSize, sortField, sortDirection],
  );
  const { onChange, onPressEnter } = useDebouncedSearch(handleSearch);

  const ingredients = data?.ingredients.ingredients ?? [];
  const total = data?.ingredients.total ?? 0;

  return (
    <Flex gap="middle" vertical>
      <Typography.Title level={3}>
        <ExperimentOutlined /> {t("ingredients.title")}
      </Typography.Title>
      <Input
        allowClear
        defaultValue={search}
        onChange={onChange}
        onPressEnter={onPressEnter}
        placeholder={t("ingredients.searchPlaceholder")}
        prefix={<SearchOutlined />}
        size="large"
      />
      <Space wrap>
        <Button icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)} type="primary">
          {t("ingredients.newIngredient")}
        </Button>
      </Space>

      <Table
        bordered
        columns={[
          {
            title: t("ingredients.name"),
            key: "name",
            sorter: true,
            sortDirections: tableSortDirections,
            sortOrder: table.sortOrderFor("name"),
            render: (_, record) => <Link to={routes.ingredientDetail.to(record.id)}>{record.name}</Link>,
          },
          {
            title: t("ingredients.brand"),
            key: "brand",
            render: (_, record) => record.brand ?? "—",
          },
          {
            title: t("ingredients.description"),
            key: "description",
            render: (_, record) => {
              const desc = record.description;
              return desc ? (desc.length > 80 ? `${desc.slice(0, 80)}…` : desc) : "—";
            },
          },
          {
            title: t("ingredients.createdAt"),
            key: "createdAt",
            sorter: true,
            sortDirections: tableSortDirections,
            sortOrder: table.sortOrderFor("createdAt"),
            render: (_, record) => formatDateTime(record.createdAt, i18n.language),
          },
          {
            title: t("ingredients.updatedAt"),
            key: "updatedAt",
            sorter: true,
            sortDirections: tableSortDirections,
            sortOrder: table.sortOrderFor("updatedAt"),
            render: (_, record) => formatDateTime(record.updatedAt, i18n.language),
          },
          {
            title: t("ingredients.actions"),
            key: "actions",
            fixed: "right",
            width: 1,
            render: (_, record) => (
              <Tooltip destroyOnHidden title={t("ingredients.delete")}>
                <Button
                  color="danger"
                  icon={<DeleteOutlined />}
                  onClick={() =>
                    modal.confirm({
                      title: t("ingredients.deleteTitle"),
                      content: t("ingredients.deleteConfirm"),
                      okButtonProps: { danger: true, icon: <DeleteOutlined /> },
                      cancelButtonProps: { icon: <CloseOutlined /> },
                      okText: t("ingredients.delete"),
                      onOk: () => deleteIngredient({ variables: { id: record.id } }),
                    })
                  }
                  variant="outlined"
                />
              </Tooltip>
            ),
          },
        ]}
        dataSource={ingredients}
        loading={loading}
        locale={{
          emptyText: (
            <TableEmptyState
              emptyChildren={
                <Button
                  color="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setCreateModalOpen(true)}
                  variant="outlined"
                >
                  {t("ingredients.newIngredient")}
                </Button>
              }
              emptyDescription={t("ingredients.noIngredients")}
              loading={loading}
            />
          ),
        }}
        onChange={table.createTableChangeHandler({ q: search })}
        pagination={table.paginationConfig(total, { q: search })}
        rowKey="id"
        scroll={tableScroll}
        size="small"
        sticky={tableSticky}
      />

      <CreateIngredientModal
        initialName={search}
        onClose={() => setCreateModalOpen(false)}
        onCreated={(created) => void navigate(routes.ingredientDetail.to(created.id))}
        open={createModalOpen}
      />
    </Flex>
  );
};
